class WbRecoSimple {
  constructor(el) {
    this.el = el;

    if (this.el.dataset.wbrsInitialized === 'true') return;
    this.el.dataset.wbrsInitialized = 'true';

    this.fetcher = this.el.querySelector('[data-reco-fetch]');
    this.timer = null;
    this.observer = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.fetcher) {
      this.fetchRecommendations();
    } else {
      this.init();
    }
  }

  async fetchRecommendations() {
    const sectionId = this.fetcher.dataset.sectionId;
    const productId = this.fetcher.dataset.productId;
    const limit = this.fetcher.dataset.limit;
    const rootUrl = window.Shopify?.routes?.root || '/';
    const url = `${rootUrl}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}`;

    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Recommendations request failed: ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newBlock = doc.getElementById(this.el.id);

      if (newBlock && newBlock.innerHTML.trim()) {
        this.el.innerHTML = newBlock.innerHTML;
        this.init();
      } else {
        this.el.hidden = true;
      }
    } catch (error) {
      this.el.hidden = true;
    }
  }

  init() {
    this.slider = this.el.querySelector('.wb-recos__slider');
    this.slides = Array.from(this.el.querySelectorAll('[data-reco-slide]'));
    this.dotsContainer = this.el.querySelector('[data-reco-dots]');
    this.prevBtn = this.el.querySelector('.wb-recos__arrow--prev');
    this.nextBtn = this.el.querySelector('.wb-recos__arrow--next');
    this.currentIndex = 0;
    this.autoplayActive = this.el.dataset.autoplay === 'true' && !this.prefersReducedMotion;

    if (!this.slider || this.slides.length === 0) {
      this.el.hidden = true;
      return;
    }

    if (this.slides.length <= 1) {
      this.prevBtn?.classList.add('is-hidden');
      this.nextBtn?.classList.add('is-hidden');
    }

    this.buildDots();
    this.bindEvents();
    this.initObserver();
    this.initCart();
    this.updateUI();

    if (this.autoplayActive && this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  buildDots() {
    if (!this.dotsContainer || this.slides.length <= 1) return;

    this.dotsContainer.innerHTML = '';

    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'wb-recos__dot';
      dot.setAttribute('aria-label', `Aller au produit ${index + 1}`);
      dot.addEventListener('click', () => this.goTo(index));
      this.dotsContainer.appendChild(dot);
    });
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    this.slider.addEventListener('mouseenter', () => this.stopAutoplay());
    this.slider.addEventListener('mouseleave', () => this.startAutoplay());
    this.slider.addEventListener('focusin', () => this.stopAutoplay());
    this.slider.addEventListener('focusout', () => this.startAutoplay());

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    });
  }

  initObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const index = this.slides.indexOf(entry.target);
        if (index < 0) return;

        this.currentIndex = index;
        this.updateUI();
      });
    }, {
      root: this.slider,
      threshold: 0.6
    });

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  goTo(index) {
    if (!this.slides.length) return;

    let target = index;

    if (target < 0) target = this.slides.length - 1;
    if (target >= this.slides.length) target = 0;

    this.currentIndex = target;

    this.slides[target]?.scrollIntoView({
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start'
    });

    this.updateUI();
  }

  updateUI() {
    const dots = this.dotsContainer?.querySelectorAll('.wb-recos__dot') || [];

    dots.forEach((dot, index) => {
      const active = index === this.currentIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  startAutoplay() {
    if (!this.autoplayActive || this.slides.length <= 1 || document.hidden) return;

    this.stopAutoplay();
    this.timer = window.setInterval(() => {
      this.goTo(this.currentIndex + 1);
    }, 3500);
  }

  stopAutoplay() {
    if (!this.timer) return;

    window.clearInterval(this.timer);
    this.timer = null;
  }

  initCart() {
    this.el.querySelectorAll('[data-reco-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const button = form.querySelector('button[type="submit"]');
        if (!button || button.disabled || button.classList.contains('is-loading')) return;

        button.classList.add('is-loading');
        button.disabled = true;

        try {
          const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            body: new FormData(form),
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json'
            }
          });

          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        } catch (error) {
          document.dispatchEvent(new CustomEvent('cart:error', { detail: { error } }));
        } finally {
          button.classList.remove('is-loading');
          button.disabled = false;
        }
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.wb-recos').forEach((el) => new WbRecoSimple(el));
});

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.wb-recos').forEach((el) => new WbRecoSimple(el));
});